import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// Components
import { ProductTypeConstant } from '@app/core/_constant/product-type.constants';
import { RfqFormComponent } from 'v2/modules/rfq/components/rfq-form/rfq-form.component';
import { RfqVariantFormComponent } from 'v2/modules/rfq/components/rfq-variant-form/rfq-variant-form.component';

// Configs
import { IChannel } from 'v2/modules/shared';
import { RfqApiService } from 'v2/modules/shared/services/api/rfq/rfq-api.service';
import { LoadingDialogUtilService } from 'v2/modules/shared/services/util/loading-dialog-util.service';

import { DialogConfig } from '../../../shared/configs/dialog.config';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';

// Interfaces

import { LocalStorageKeys } from '../../../shared/interfaces/localstorage.interface';

// Services
import { ChannelProductMappingApiService } from '../../../shared/services/api/channel/channel-product-mapping-api.service';

// Utils
import { UiConfirmationDialogComponent } from '../../../ui';
import {
  ChannelMappingGridOptions,
  PAUSE_BUFFER_QUANTITY,
} from '../../configs/channel-mapping.config';
import {
  EChannelProductAgFilter,
  EChannelPushStockOMSProduct,
  EProductMappingType,
  IAppIntegrationAccountProductOverride,
  IChannelProductMappingFeatureFlag,
  IChannelProductMappingV2,
} from '../../interfaces';

import { AppIntegrationAccountProductsApiService } from './../../../shared/services/api/app-integration/app-integration-account-products-api.service';
import { AgGridUtils } from './../../../shared/util/ag-grid.utils';
import { ChannelAgGridConfig } from './../../configs/channel-ag-grid.config';

@Component({
  selector: 'lum-channel-product-mapping-unified',
  templateUrl: './channel-product-mapping-unified.component.html',
  styleUrls: ['./channel-product-mapping-unified.component.scss'],
})
export class ChannelProductMappingUnifiedComponent implements OnInit, OnDestroy {
  @Input()
  appIntegrationAccountId :number | null = null;

  @Input()
  channel :IChannel | null = null;

  @Input()
  featureFlags :IChannelProductMappingFeatureFlag;

  @Input()
  mappingType :EProductMappingType = EProductMappingType.CHANNEL;

  @Output()
  buildParams = new EventEmitter<any>();

  @Output()
  selectionChanged = new EventEmitter<any>();

  columnDefs :ColDef[] = [];
  syncLoading :boolean = false;

  gridOptions :GridOptions = ChannelMappingGridOptions;
  allowedCellUpdates = [
    EChannelPushStockOMSProduct.BUFFER_QUANTITY,
    EChannelPushStockOMSProduct.LEAD_TIME_DAYS,
    EChannelPushStockOMSProduct.OMS_DEFAULT_UNIT,
  ];
  selectedRows :IAppIntegrationAccountProductOverride[] = [];

  private gridApi :GridApi;
  private onDestroy$ = new Subject<void>();

  constructor(
    private cd :ChangeDetectorRef,
    private channelProductMappingApiService :ChannelProductMappingApiService,
    private dialog :MatDialog,
    private loadingDialogService :LoadingDialogUtilService,
    private snackBar :MatSnackBar,
    private rfqApiService :RfqApiService,
    private appIntegrationAccountProductsApiService :AppIntegrationAccountProductsApiService,
  ) {}

  ngOnInit() :void {
    this.columnDefs = ChannelAgGridConfig.buildProductMappingAgGridColDefs({
      buildParams: {
        actionsCellParams: {
          createSku: this.createSku.bind(this),
          addVariantSku: this.addVariantSku.bind(this),
          addAlternateSku: this.addAlternateSku.bind(this),
          pushSingleProduct: this.pushSingleProduct.bind(this),
          toggleOverBuffer: this.toggleOverBuffer.bind(this),
        },
      },
      channel: this.channel,
    })[this.mappingType];

    if (this.channel) {
      this.columnDefs = this.columnDefs.map((column) => {
        if (column.field === EChannelPushStockOMSProduct.CHANNEL) {
          column.filter = false;
        }

        return column;
      });
    }
  }

  private onSetDefaultUnit(item :any) :void {
    console.log('onSetDefault', item);
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onGridReady(params :GridReadyEvent) :void {
    params.api.setFilterModel({
      rfq: {
        type: 'set',
        values: [EChannelProductAgFilter.UNMAPPED],
      },
      productFlagStatus: {
        type: 'set',
        values: [EChannelProductAgFilter.ACTIVE],
      },
    });
    this.gridApi = params.api;
    const datasource = this.getServerSideDatasource();
    this.gridApi.setServerSideDatasource(datasource);
    this.attachEventListenersOnPageStateChange();
  }

  getServerSideDatasource() :IServerSideDatasource {
    let url = null;
    switch (this.mappingType) {
      case EProductMappingType.APP_INTEGRATION_ACCOUNT:
        url = '/app-integration-accounts/products';
        break;

      default:
        url = '/manage/channels/v3/products';
        break;
    }

    return {
      getRows: (params :IServerSideGetRowsParams) => {
        this.channelProductMappingApiService
          .getProductsUnified({
            url,
            params: {
              ...AgGridUtils.mapQueryParams({
                params,
                paginationPageSize: this.gridOptions.paginationPageSize,
                relations: ChannelAgGridConfig.relations[this.mappingType],
              }),
              channelId: this.channel?.id || '',
              omsProductsOnly:
                this.mappingType === EProductMappingType.APP_INTEGRATION_ACCOUNT,
              appIntegrationAccountId: this.appIntegrationAccountId,
            },
          })
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((response) => {
            params.successCallback(response.data, response.meta.total);
          });
      },
    };
  }

  onCellValueChanged(params :any) :void {
    if (
      params?.oldValue !== params?.newValue &&
      this.allowedCellUpdates.includes(params.colDef.field)
    ) {
      const disallowedEmptyFields = ['bufferQuantity', 'leadtimeDays'];
      if (
        params.colDef.field &&
        disallowedEmptyFields.includes(params.colDef.field) &&
        params.newValue === ''
      ) {
        return;
      }
      const payload :any = {};
      const { data } = params;
      if (data?.channelId) {
        payload.channelId = data?.channelId;
      }

      if (data?.bufferQuantity) {
        payload.bufferQuantity = data?.bufferQuantity;
      }

      if (data?.leadtimeDays) {
        payload.leadtimeDays = data?.leadtimeDays;
      }

      if (params.colDef.field === EChannelPushStockOMSProduct.BUFFER_QUANTITY) {
        payload.bufferQuantity = params.newValue;
      } else if (
        params.colDef.field === EChannelPushStockOMSProduct.LEAD_TIME_DAYS
      ) {
        payload.leadtimeDays = params.newValue;
      } else if (
        params.colDef.field === EChannelPushStockOMSProduct.OMS_DEFAULT_UNIT
      ) {
        payload.defaultUomId = params.newValue.id;
      }

      switch (this.mappingType) {
        case EProductMappingType.CHANNEL:
          this.channelProductMappingApiService
            .saveChannelProductOverride(params.data.id, payload)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response) => {
                this.snackBar.open('Successfully saved.', 'Success', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
              },
              error: (error) => {
                this.snackBar.open('Failed to save changes.', 'Error', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
              },
            });
          break;

        case EProductMappingType.APP_INTEGRATION_ACCOUNT:
          this.appIntegrationAccountProductsApiService
            .update(params.data.id, payload)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response) => {
                this.snackBar.open('Successfully saved.', 'Success', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
              },
              error: (error) => {
                this.snackBar.open('Failed to save changes.', 'Error', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
              },
            });
          break;

        default:
          break;
      }
    }
  }

  onSelectionChanged() :void {
    const selectedRows = [];
    this.gridOptions.api.forEachNode((node :any) => {
      if (node.selected) {
        selectedRows.push(node.data);
      }
    });

    this.selectedRows = selectedRows;
    this.selectionChanged.emit(selectedRows);
  }

  private attachEventListenersOnPageStateChange() :void {
    // attach event listeners on filter, sort, column visibility change
    this.gridOptions.api?.addEventListener(
      'filterChanged',
      this.savePageState.bind(this),
    );
    this.gridOptions.api?.addEventListener(
      'sortChanged',
      this.savePageState.bind(this),
    );
    this.gridOptions.api?.addEventListener(
      'columnVisible',
      this.savePageState.bind(this),
    );
    this.gridOptions.api?.addEventListener(
      'columnPinned',
      this.savePageState.bind(this),
    );
  }

  private savePageState() :void {
    const filterModel = this.gridOptions.api?.getFilterModel();
    const columnState = this.gridOptions.columnApi?.getColumnState();
    const pinnedColumns = {
      left: this.gridOptions.columnApi
        ?.getDisplayedLeftColumns()
        .map((column) => column.getColId()),
      right: this.gridOptions.columnApi
        ?.getDisplayedRightColumns()
        .map((column) => column.getColId()),
    };

    const pageState = {
      filterModel,
      columnState,
      pinnedColumns,
    };

    localStorage.setItem(
      LocalStorageKeys.CHANNEL_PRODUCT_MAPPING_PAGE_STATE, // to be changed
      JSON.stringify(pageState),
    );
  }

  private createSku(data :IChannelProductMappingV2) :void {
    this.dialog
      .open(RfqFormComponent, {
        data: {
          featureFlag: {
            newKitsStructureEnabled:
              this.featureFlags?.newKitsStructureEnabled || false,
            lotTrackingEnabled: this.featureFlags?.lotTrackingEnabled || false,
            customFieldsEnabled: this.featureFlags?.customFieldsEnabled || false,
          },
          permissions: [],
          initialData: {
            internal_sku: data.sku,
            product_name: data.productName,
          },
        },
        width: DialogConfig.sizes['xx-large'],
        height: 'calc(100vh - 150px)',
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  private addVariantSku(data :IChannelProductMappingV2) :void {
    this.dialog
      .open(RfqVariantFormComponent, {
        data: {
          featureFlag: {
            newKitsStructureEnabled:
              this.featureFlags?.newKitsStructureEnabled || false,
            lotTrackingEnabled: this.featureFlags?.lotTrackingEnabled || false,
          },
          productType: ProductTypeConstant.PRODUCT,
          discontinued: 0,
        },
        width: DialogConfig.sizes.medium,
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  private addAlternateSku(data :IChannelProductMappingV2) :void {
    let rfq = null;

    switch (this.mappingType) {
      case EProductMappingType.CHANNEL:
        rfq = data.productOverride?.lumSku || data.lumSku;
        break;

      default:
        break;
    }

    this.rfqApiService
      .addAlternateSku({
        rfqId: rfq?.id,
        sku: rfq?.internalSku,
        alternateSku: data.sku,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.gridApi.refreshServerSide();
          this.snackBar.open(`Alternate sku successfully added.`, 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (response) => {
          const errorMessage = response.error ?? 'Failed to add alternate sku.';
          this.snackBar.open(errorMessage, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private pushSingleProduct(data :IChannelProductMappingV2) :void {
    this.loadingDialogService.show(`Pushing ${data.productName}...`);
    this.pushProductUpdates([
      {
        channelId: data.channelId,
        productIds: [data.id],
      },
    ])
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loadingDialogService.close();
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          const { success } = response;
          this.snackBar.open(
            success || 'Successfully pushed product updates.',
            'Success',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
        error: (err) => {
          const { error } = err;
          this.snackBar.open(error || 'Failed to push updates.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private pushProductUpdates(
    data :{
      channelId :number;
      productIds :number[];
    }[],
  ) :Observable<any> {
    return this.channelProductMappingApiService.pushUpdates({
      items: data,
    });
  }

  private toggleOverBuffer(
    data :IChannelProductMappingV2,
    toggled :boolean = false,
  ) :void {
    const messageObject = toggled
      ? {
          type: 'Pause',
          message:
            'This will set the buffer quantity super high to prevent the ' +
            'product from being sold.',
          value: PAUSE_BUFFER_QUANTITY,
        }
      : {
          type: 'Resume',
          message:
            'This will set the buffer quantity to 0.  If you do not have ' +
            'sufficient available quantity, sales may still not be possible.',
          value: 0,
        };
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: messageObject.type,
          message: messageObject.message,
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.updateBufferQuantity(data, messageObject.value);
        }
      });
  }

  private updateBufferQuantity(
    data :IChannelProductMappingV2,
    bufferQuantity :number,
  ) :void {
    data.bufferQuantity = bufferQuantity;
    this.loadingDialogService.show(`Toggling buffer for ${data.productName}...`);
    this.channelProductMappingApiService
      .saveChannelProductOverride(data.id, {
        channelId: data.channelId.toString(),
        bufferQuantity: data.bufferQuantity.toString(),
      })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loadingDialogService.close();
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          this.gridApi.refreshServerSide();
          this.snackBar.open(
            'Successfully updated buffer quantity for product.',
            'Success',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
        error: (err) => {
          const { error } = err;
          this.snackBar.open(error || 'Failed to update product.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }
}
